<div class="dialog-container">
  <div class="dialog-header">
    <h2 class="dialog-title">
      {{ (data.mode === 'create' ? 'fee.create_subgroup' : 'fee.edit_subgroup') | translate }}
    </h2>
    <button class="close-btn" type="button" (click)="cancel()" [attr.aria-label]="'common.close_dialog' | translate">
      <mat-icon>close</mat-icon>
    </button>
  </div>

  <form [formGroup]="form" (ngSubmit)="submit()" class="dialog-form">
    <div class="grid two-col">
      <mat-form-field appearance="outline">
        <mat-label>{{ 'fee.subgroup_name' | translate }}</mat-label>
        <input matInput formControlName="name" />
      </mat-form-field>
      <div class="price-field-row">
        <mat-form-field appearance="outline">
          <mat-label>{{ 'fee.price' | translate }}</mat-label>
          <input matInput type="number" formControlName="price" min="0.01" />
        </mat-form-field>
        <mat-checkbox class="allow-custom-amount-checkbox" color="primary" formControlName="allowCustomAmount">
          {{ 'fee.allow_custom_amount' | translate }}
        </mat-checkbox>
      </div>
    </div>

    <div class="grid three-col">
      <mat-form-field appearance="outline">
        <mat-label>{{ 'fee.mentor_name_optional' | translate }}</mat-label>
        <input matInput formControlName="mentorName" />
      </mat-form-field>
      <mat-form-field appearance="outline">
        <mat-label>{{ 'fee.mentor_phone_optional' | translate }}</mat-label>
        <input matInput formControlName="mentorPhone" />
      </mat-form-field>
      <mat-form-field appearance="outline">
        <mat-label>{{ 'fee.mentor_mail_optional' | translate }}</mat-label>
        <input matInput formControlName="mentorEmail" />
      </mat-form-field>
    </div>

    <div class="grid two-col">
      <mat-form-field class="sg-date-field" appearance="fill">
        @if (isDailySinglePay) {
          <input matInput [matDatepicker]="singlePicker" [min]="singlePickerMin" formControlName="startDate" [placeholder]="'fee.date' | translate" />
          <mat-datepicker-toggle matIconSuffix [for]="singlePicker"></mat-datepicker-toggle>
          <mat-datepicker #singlePicker></mat-datepicker>
        } @else {
          <mat-date-range-input [formGroup]="form" [rangePicker]="picker" [min]="minStartDate">
            <input matStartDate formControlName="startDate" [placeholder]="'fee.start_date' | translate" />
            <input matEndDate formControlName="endDate" [placeholder]="'fee.end_date' | translate" />
          </mat-date-range-input>
          <mat-datepicker-toggle matIconSuffix [for]="picker"></mat-datepicker-toggle>
          <mat-date-range-picker #picker></mat-date-range-picker>
        }
      </mat-form-field>
      <mat-form-field appearance="outline">
        <mat-label>{{ 'fee.frequency' | translate }}</mat-label>
        <mat-select formControlName="frequency">
          @for (freq of frequencyTypes; track freq) {
            <mat-option [value]="freq">{{ frequencyLabel(freq) }}</mat-option>
          }
        </mat-select>
      </mat-form-field>
    </div>

    <mat-form-field appearance="outline" class="full-width">
      <mat-label>{{ 'fee.payment_flow' | translate }}</mat-label>
      <mat-select formControlName="paymentFlow">
        @for (opt of paymentFlowOptions; track opt.value) {
          <mat-option [value]="opt.value">{{ opt.labelKey | translate }}</mat-option>
        }
      </mat-select>
    </mat-form-field>

    <h3 class="section-title">{{ 'fee.edit_discount' | translate }}</h3>

    <div class="discount-grid">
      <mat-form-field appearance="outline">
        <mat-label>{{ 'fee.subgroups.discount_type' | translate }}</mat-label>
        <mat-select formControlName="discountType" (openedChange)="onDiscountSelectOpened($event)">
          @for (type of discountTypes; track type) {
            <mat-option [value]="type" (click)="onDiscountTypeOptionClick(type)">{{ 'fee.subgroups.discount_types.' + type | translate }}</mat-option>
          }
        </mat-select>
        @if (form.get('discountType')!.hasError('discountTypeRequired')) {
          <mat-error>{{ 'fee.validation.discount_type_required' | translate }}</mat-error>
        }
      </mat-form-field>

      <mat-form-field appearance="outline">
        <mat-label>{{ 'fee.discount' | translate }}</mat-label>
        <input matInput type="number" formControlName="discountValue" min="0" class="no-spinner"
               inputmode="numeric" (keydown)="onDiscountKeyDown($event)" (paste)="onDiscountPaste($event)" />
      </mat-form-field>

      <div class="price-summary">
        <span class="price-label">{{ 'fee.price' | translate }} - {{ (form.get('price')!.value || 0) | number:'1.0-0' }} ֏</span>
        <span class="final-price-label">{{ 'fee.final_price' | translate }} - {{ finalPrice | number:'1.0-0' }} ֏</span>
      </div>
    </div>

    <mat-form-field appearance="outline" class="full-width">
      <mat-label>{{ 'fee.discount_reason' | translate }}</mat-label>
      <input matInput formControlName="reason" maxlength="30"
             (keydown)="onReasonKeyDown($event)" (paste)="onReasonPaste($event)" />
      <mat-hint align="end">{{ form.get('reason')!.value?.length || 0 }}/30</mat-hint>
      @if (form.get('reason')!.hasError('maxlength')) {
        <mat-error>{{ 'fee.validation.max_30_characters' | translate }}</mat-error>
      }
    </mat-form-field>

    <div class="error-stack">
      @if (form.get('name')!.hasError('required') && form.get('name')!.touched) {
        <mat-error>{{ 'fee.validation.subgroup_name_required' | translate }}</mat-error>
      }
      @if (form.get('price')!.hasError('required') && form.get('price')!.touched) {
        <mat-error>{{ 'fee.validation.price_required' | translate }}</mat-error>
      }
      @if ((form.get('startDate')!.hasError('required') && form.get('startDate')!.touched) ||
      (!isDailySinglePay && form.get('endDate')!.hasError('required') && form.get('endDate')!.touched)) {
        <mat-error>{{ 'fee.validation.select_timeframe' | translate }}</mat-error>
      }
      @if (form.get('startDate')!.hasError('matDatepickerMin') ||
      (!isDailySinglePay && form.get('endDate')!.hasError('matDatepickerMin'))) {
        <mat-error>{{ 'fee.validation.date_in_past' | translate }}</mat-error>
      }
      @if (form.get('frequency')!.hasError('required') && form.get('frequency')!.touched) {
        <mat-error>{{ 'fee.validation.frequency_required' | translate }}</mat-error>
      }
      @if (form.get('discountValue')!.hasError('notInteger')) {
        <mat-error>{{ 'fee.validation.discount_whole_number' | translate }}</mat-error>
      }
      @if (form.get('discountValue')!.hasError('maxPercent')) {
        <mat-error>{{ 'fee.validation.discount_percent_max' | translate }}</mat-error>
      }
      @if (form.get('discountValue')!.hasError('exceedsPrice')) {
        <mat-error>{{ 'fee.validation.discount_less_than_price' | translate }}</mat-error>
      }
    </div>

    <div class="dialog-actions">
      <button type="button" class="btn-cancel" (click)="cancel()">{{ 'Cancel' | translate }}</button>
      <button type="submit" class="btn-save" [disabled]="submitting">{{ 'user_card.save' | translate }}</button>
    </div>

  </form>
</div>
